// SPDX-License-Identifier: AGPL-3.0-or-later

import {getSteamIconUrlCandidates, sanitizeGameIconDataUrl} from '@app/features/game_activity/GameActivity';
import {GameControllerIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import type React from 'react';
import {useEffect, useMemo, useState} from 'react';

interface GameActivityIconProps {
	gameName: string;
	steamAppId?: string | null;
	steamIconHash?: string | null;
	iconDataUrl?: string | null;
	className?: string;
	imageClassName?: string;
	fallbackClassName?: string;
	size?: number;
	preventContextMenu?: boolean;
	variant?: 'glyph' | 'artwork';
}

export function GameActivityIcon({
	gameName,
	steamAppId,
	steamIconHash,
	iconDataUrl,
	className,
	imageClassName,
	fallbackClassName,
	size = 12,
	preventContextMenu = false,
	variant = 'artwork',
}: GameActivityIconProps) {
	const handleContextMenu = preventContextMenu
		? (event: React.MouseEvent) => {
				event.preventDefault();
			}
		: undefined;
	const iconUrls = useMemo(() => {
		if (variant === 'glyph') {
			return [];
		}
		const urls: Array<string> = [];
		const localIcon = sanitizeGameIconDataUrl(iconDataUrl);
		if (localIcon) {
			urls.push(localIcon);
		}
		if (steamAppId) {
			for (const url of getSteamIconUrlCandidates(steamAppId, steamIconHash)) {
				if (!urls.includes(url)) {
					urls.push(url);
				}
			}
		}
		return urls;
	}, [variant, iconDataUrl, steamAppId, steamIconHash]);
	const [failedCount, setFailedCount] = useState(0);
	useEffect(() => {
		setFailedCount(0);
	}, [variant, iconDataUrl, steamAppId, steamIconHash]);
	const currentUrl = iconUrls[failedCount] ?? null;

	if (!currentUrl) {
		return (
			<GameControllerIcon
				weight="fill"
				className={clsx(className, fallbackClassName)}
				size={size}
				aria-hidden
			/>
		);
	}

	return (
		<img
			src={currentUrl}
			alt=""
			width={size}
			height={size}
			draggable={false}
			referrerPolicy="no-referrer"
			className={clsx(className, imageClassName)}
			onContextMenu={handleContextMenu}
			onError={() => {
				setFailedCount((count) => Math.min(count + 1, iconUrls.length));
			}}
			aria-hidden
			data-game-name={gameName}
		/>
	);
}
